import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Stack,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { demoPreviewBootStorageKey } from '../../utils/demoPreviewStorage';

/** @typedef {'all' | 'company' | 'domain' | 'user_id' | 'role'} DemoFilterMode */

/**
 * @param {Array<{ hosting_account_id: string, company_name: string, account_code?: string, domains: Array<{ domain: string, users: any[] }> }>} trees
 * @param {DemoFilterMode} mode
 * @param {string} rawQuery
 */
function filterDemoTrees(trees, mode, rawQuery) {
  const query = String(rawQuery || '').trim();
  if (!query) return trees;
  const ql = query.toLowerCase();

  const rowMatches = (row, hcName, dom) => {
    const id = row.user?.id ? String(row.user.id).toLowerCase() : '';
    const email = String(row.email || '').toLowerCase();
    const fn = String(row.user?.first_name || '').toLowerCase();
    const ln = String(row.user?.last_name || '').toLowerCase();
    const group = String(row.group || '').toLowerCase();
    const job = String(row.job_title || '').toLowerCase();
    if (mode === 'user_id') return id && (id === ql || id.includes(ql));
    if (mode === 'role') return group.includes(ql) || job.includes(ql);
    if (mode === 'company') return true;
    if (mode === 'domain') return true;
    return (
      hcName.includes(ql) ||
      dom.includes(ql) ||
      (id && id.includes(ql)) ||
      email.includes(ql) ||
      fn.includes(ql) ||
      ln.includes(ql) ||
      group.includes(ql) ||
      job.includes(ql)
    );
  };

  return trees
    .map((hc) => {
      const hcName = String(hc.company_name || '').toLowerCase();
      if (mode === 'company' && !hcName.includes(ql)) return null;

      const domains = (hc.domains || [])
        .map((block) => {
          const dom = String(block.domain || '').toLowerCase();
          if (mode === 'domain' && !dom.includes(ql)) return null;

          if (mode === 'company' || mode === 'domain') {
            return block;
          }

          const users = (block.users || []).filter((row) => rowMatches(row, hcName, dom));
          if (users.length === 0) return null;
          return { ...block, users };
        })
        .filter(Boolean);

      if (domains.length === 0) return null;
      return { ...hc, domains };
    })
    .filter(Boolean);
}

function buildDemoTemplateTree(users) {
  const byEmail = new Map(users.map((u) => [u.email, u]));
  const roots = users.filter((u) => {
    if (!u.reports_to_email) return true;
    return !byEmail.has(u.reports_to_email);
  });
  const childrenByMgr = new Map();
  for (const u of users) {
    const mgr = u.reports_to_email;
    if (!mgr || !byEmail.has(mgr)) continue;
    if (!childrenByMgr.has(mgr)) childrenByMgr.set(mgr, []);
    childrenByMgr.get(mgr).push(u);
  }
  for (const [, list] of childrenByMgr) {
    list.sort((a, b) => String(a.email).localeCompare(String(b.email)));
  }
  roots.sort((a, b) => String(a.email).localeCompare(String(b.email)));
  return { roots, childrenByMgr };
}

function DemoExplorerBranch({
  node,
  childrenByMgr,
  onPreview,
  previewBusyId,
  collapsedKeys,
  toggleCollapsed,
  previewOpenMode,
}) {
  const hasUser = Boolean(node.user?.id);
  const kids = childrenByMgr.get(node.email) || [];
  const rowKey = node.email;
  const hasBranch = kids.length > 0;
  const collapsed = collapsedKeys.has(rowKey);

  return (
    <Box sx={{ py: 0.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            width: 32,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: 0.25,
          }}
        >
          {hasBranch ? (
            <IconButton
              aria-label={collapsed ? 'Expand' : 'Collapse'}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapsed(rowKey);
              }}
              sx={{ p: 0.25 }}
            >
              {collapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, height: 28 }} aria-hidden />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <ListItemButton disabled={!hasUser} sx={{ borderRadius: 1, py: 0.5, alignItems: 'flex-start', flex: 1 }}>
            <ListItemAvatar>
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                {(node.user?.first_name || node.email || '?')[0]}
                {(node.user?.last_name || '')?.[0]}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <span>
                    {node.user
                      ? `${node.user.first_name || ''} ${node.user.last_name || ''}`.trim() || node.email
                      : node.email}
                  </span>
                  <Chip size="small" label={node.group || '—'} variant="outlined" sx={{ height: 22 }} />
                  {!hasUser ? (
                    <Chip size="small" label="Not seeded" color="warning" variant="outlined" sx={{ height: 22 }} />
                  ) : null}
                </Box>
              }
              secondary={node.email}
              secondaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
            />
          </ListItemButton>
          {hasUser ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              disabled={previewBusyId === node.user.id}
              onClick={() =>
                onPreview(node.user.id, {
                  email: node.email,
                  label:
                    `${node.user?.first_name || ''} ${node.user?.last_name || ''}`.trim() || node.email,
                })
              }
              sx={{ flexShrink: 0, mt: 0.5 }}
            >
              {previewOpenMode === 'panel' ? 'Preview…' : 'View as'}
            </Button>
          ) : null}
        </Box>
      </Box>
      {hasBranch && !collapsed ? (
        <Box
          sx={(theme) => ({
            ml: 2,
            pl: 2,
            mt: 0.25,
            borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
          })}
        >
          {kids.map((child) => (
            <DemoExplorerBranch
              key={child.email}
              node={child}
              childrenByMgr={childrenByMgr}
              onPreview={onPreview}
              previewBusyId={previewBusyId}
              collapsedKeys={collapsedKeys}
              toggleCollapsed={toggleCollapsed}
              previewOpenMode={previewOpenMode}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function openDemoPreviewTarget(url, mode) {
  if (mode === 'tab') {
    return window.open(url, '_blank');
  }
  const w = 1280;
  const h = 840;
  const left = Math.max(0, Math.round((window.screen.width - w) / 2));
  const top = Math.max(0, Math.round((window.screen.height - h) / 2));
  const features = `scrollbars=yes,resizable=yes,width=${w},height=${h},left=${left},top=${top}`;
  const previewWin = window.open(url, `erp4c_demo_preview_${Date.now()}`, features);
  if (!previewWin) {
    return null;
  }
  return previewWin;
}

export default function DemoUsersActualView() {
  const [loading, setLoading] = useState(true);
  const [trees, setTrees] = useState([]);
  const [previewBusyId, setPreviewBusyId] = useState(null);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  /** @type {DemoFilterMode} */
  const [filterMode, setFilterMode] = useState('all');
  const [filterQuery, setFilterQuery] = useState('');
  /** @type {'tab' | 'popup' | 'panel'} */
  const [previewOpenMode, setPreviewOpenMode] = useState('tab');
  const [launchPanel, setLaunchPanel] = useState(null);
  /** @type {'tab' | 'popup'} */
  const [launchSubMode, setLaunchSubMode] = useState('popup');
  const [launchBusy, setLaunchBusy] = useState(false);

  const toggleCollapsed = useCallback((key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/demo-users/tree');
      setTrees(r.data?.data?.trees || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load demo users');
      setTrees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredTrees = useMemo(
    () => filterDemoTrees(trees, filterMode, filterQuery),
    [trees, filterMode, filterQuery]
  );

  const runPreviewOpen = useCallback(async (userId, openMode) => {
    const r = await api.post('/admin/demo-preview/start', { user_id: userId });
    const { token, refreshToken, viewingAs } = r.data?.data || {};
    if (!token || !viewingAs) {
      toast.error('Invalid preview response.');
      return false;
    }
    const adminToken = localStorage.getItem('token');
    const adminRefresh = localStorage.getItem('refreshToken');
    const bootId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const bootKey = demoPreviewBootStorageKey(bootId);
    localStorage.setItem(
      bootKey,
      JSON.stringify({
        adminToken,
        adminRefresh,
        demoToken: token,
        demoRefresh: refreshToken || '',
        viewingAsLabel: `${viewingAs.first_name || ''} ${viewingAs.last_name || ''} (${viewingAs.email})`.trim(),
      })
    );
    const url = `${window.location.origin}/demo-preview-entry/${encodeURIComponent(bootId)}`;
    const previewWin = openDemoPreviewTarget(url, openMode);
    if (!previewWin) {
      localStorage.removeItem(bootKey);
      toast.error('Pop-up was blocked. Allow pop-ups for this site, then try again.');
      return false;
    }
    const kind = openMode === 'tab' ? 'new tab' : 'popup window';
    toast.success(`Opening ${kind} as ${viewingAs.email}. Use Exit preview there when done.`);
    return true;
  }, []);

  const startPreview = async (userId, _meta = {}) => {
    if (previewOpenMode === 'panel') {
      setLaunchPanel({ userId, meta: _meta });
      return;
    }
    setPreviewBusyId(userId);
    try {
      await runPreviewOpen(userId, previewOpenMode);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not start preview');
    } finally {
      setPreviewBusyId(null);
    }
  };

  const confirmLaunchPanel = async () => {
    if (!launchPanel?.userId) return;
    setLaunchBusy(true);
    try {
      await runPreviewOpen(launchPanel.userId, launchSubMode);
      setLaunchPanel(null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not start preview');
    } finally {
      setLaunchBusy(false);
    }
  };

  const totalDomainsRaw = useMemo(
    () => trees.reduce((n, t) => n + (Array.isArray(t.domains) ? t.domains.length : 0), 0),
    [trees]
  );

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Demo / Test Users — Actual View
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Template accounts grouped by <strong>hosting company</strong> and <strong>domain</strong>, with a reporting tree
        (manager → reports). Choose how previews open below. Your administrator session stays in this tab; use{' '}
        <strong>Exit preview</strong> in the preview window when finished.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Only users that exist in the database (after seeding) can be previewed. Preview is limited to demo template
        mailboxes on active hosting domains. Allow pop-ups if the preview window does not open.         The preview session shows a <strong>red frame</strong> and banner so you know you are in{' '}
        <strong>Users preview mode</strong>. Your administrator session in the original tab is unchanged (preview uses this
        tab&apos;s session storage for the demo JWT so shared browser storage does not overwrite your admin login).
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Filter tree
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="demo-filter-mode">Match on</InputLabel>
            <Select
              labelId="demo-filter-mode"
              label="Match on"
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
            >
              <MenuItem value="all">All fields (free text)</MenuItem>
              <MenuItem value="company">Hosting company name</MenuItem>
              <MenuItem value="domain">Domain name</MenuItem>
              <MenuItem value="user_id">User ID (UUID)</MenuItem>
              <MenuItem value="role">Template group / job title</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label="Search"
            placeholder={
              filterMode === 'user_id'
                ? 'Paste or type user UUID…'
                : filterMode === 'company'
                  ? 'Company name…'
                  : filterMode === 'domain'
                    ? 'example.com…'
                    : filterMode === 'role'
                      ? 'Manager, HR, …'
                      : 'Company, domain, email, name, id, group…'
            }
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Open preview as
        </Typography>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 360 } }}>
          <InputLabel id="demo-preview-open-mode">Preview target</InputLabel>
          <Select
            labelId="demo-preview-open-mode"
            label="Preview target"
            value={previewOpenMode}
            onChange={(e) => setPreviewOpenMode(e.target.value)}
          >
            <MenuItem value="tab">New browser tab</MenuItem>
            <MenuItem value="popup">Popup window (centered, scrollbars)</MenuItem>
            <MenuItem value="panel">In-app launch panel (red dialog, then open)</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          <strong>Popup</strong> uses a separate window with horizontal/vertical scroll bars. <strong>In-app launch panel</strong>{' '}
          opens a scrollable red-bordered dialog in this application where you confirm and choose tab vs popup (embedded
          full SPA is not used, because sessions share browser storage).
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : trees.length === 0 || totalDomainsRaw === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            No active hosting domains found. Add domains under Administration → Settings → Company domains, seed demo users
            under SEED Data, then refresh this page.
          </Typography>
        </Paper>
      ) : filteredTrees.length === 0 ? (
        <Alert severity="warning">
          No rows match the current filter. Clear the search or change <strong>Match on</strong>.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {filteredTrees.map((hc) => (
            <Accordion key={hc.hosting_account_id} defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <BusinessIcon fontSize="small" color="primary" />
                  <Typography sx={{ fontWeight: 700 }} noWrap>
                    {hc.company_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hc.account_code}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {(hc.domains || []).map((block) => {
                  const { roots, childrenByMgr } = buildDemoTemplateTree(block.users || []);
                  return (
                    <Box key={block.domain} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, mt: 0.5 }}>
                        <FolderIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          @{block.domain}
                        </Typography>
                        <Chip size="small" label={`${(block.users || []).length} template`} variant="outlined" sx={{ height: 22 }} />
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),
                          borderColor: 'divider',
                        }}
                      >
                        {roots.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            No template rows.
                          </Typography>
                        ) : (
                          roots.map((root) => (
                            <DemoExplorerBranch
                              key={root.email}
                              node={root}
                              childrenByMgr={childrenByMgr}
                              onPreview={startPreview}
                              previewBusyId={previewBusyId}
                              collapsedKeys={collapsedKeys}
                              toggleCollapsed={toggleCollapsed}
                              previewOpenMode={previewOpenMode}
                            />
                          ))
                        )}
                      </Paper>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      <Dialog
        open={Boolean(launchPanel)}
        onClose={() => !launchBusy && setLaunchPanel(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            border: '4px solid #c62828',
            borderRadius: 2,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: '#ffcdd2', color: '#b71c1c', fontWeight: 800 }}>
          Users preview mode — Administrator
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: '#fff8f8',
            overflow: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          <Alert severity="error" sx={{ mb: 2, fontWeight: 600 }}>
            You are about to open a live preview as a demo user. The preview window will be framed in red. This dialog is
            scrollable if your screen is small.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>User</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-word' }}>
            {launchPanel?.meta?.label || '—'} · {launchPanel?.meta?.email || '—'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Open as</strong>
          </Typography>
          <RadioGroup value={launchSubMode} onChange={(e) => setLaunchSubMode(e.target.value)}>
            <FormControlLabel value="tab" control={<Radio color="error" />} label="New browser tab" />
            <FormControlLabel
              value="popup"
              control={<Radio color="error" />}
              label="Popup window (centered, with scroll bars)"
            />
          </RadioGroup>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            ID: <span style={{ fontFamily: 'monospace' }}>{launchPanel?.userId}</span>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#ffebee', flexShrink: 0 }}>
          <Button onClick={() => !launchBusy && setLaunchPanel(null)} disabled={launchBusy}>
            Cancel
          </Button>
          <Button variant="contained" color="error" disabled={launchBusy} onClick={confirmLaunchPanel}>
            {launchBusy ? <CircularProgress size={22} color="inherit" /> : 'Open preview'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
